import classNames from 'classnames';
import { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './hotel-facilities.module.scss';

export interface HotelFacilitiesProps extends ModuleWrapperProps {
  /** The text to show as a description */
  description?: string;
  /** The text to show as a headline */
  headline?: string;
  /** The sections to show */
  sections?: {
    facilities?: {
      facilityIcon: IconName;
      facilityId: number;
      facilityName: string;
    }[];
    headline: string;
    icon?: IconName;
  }[];
}

const ITEM_LIMIT = 6;

const HotelFacilities = ({
  description,
  headline,
  sections = [],
  ...rest
}: HotelFacilitiesProps) => {
  const { t } = useI18n();
  const [expandedLists, setExpandedLists] = useState<number[]>([]);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <Row className={styles.itemsRow}>
          <Column className={styles.textColumn} width={{ desktop: 3 }}>
            <Row className={styles.itemsRow}>
              <Column
                className={styles.textColumn}
                width={{ desktop: 12, tablet: 6 }}
              >
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>

                {description && <RichText text={description} />}
              </Column>
            </Row>
          </Column>

          <Column
            className={styles.textColumn}
            offset={{ desktop: 1 }}
            width={{ desktop: 8 }}
          >
            <Row className={styles.itemsRow}>
              {sections.map((section, sectionIndex) => (
                <Column
                  key={sectionIndex}
                  className={styles.itemColumn}
                  width={{ tablet: 4 }}
                >
                  <div className={styles.itemHeader}>
                    {section.icon && (
                      <Icon
                        className={styles.itemHeaderIcon}
                        name={section.icon}
                      />
                    )}

                    <Typography
                      className={styles.itemHeaderText}
                      tagStyle="bodyLargeBold"
                    >
                      {section.headline}
                    </Typography>
                  </div>

                  {section.facilities && section.facilities.length > 0 && (
                    <ul
                      className={classNames(styles.itemFooter, {
                        [styles.isExpanded]:
                          expandedLists.includes(sectionIndex),
                      })}
                    >
                      {section.facilities.map((subItem, subItemIndex) => (
                        <li
                          key={subItemIndex}
                          className={styles.itemFooterItem}
                        >
                          <IconLabel
                            icon={subItem.facilityIcon}
                            text={subItem.facilityName}
                          />
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.facilities &&
                    section.facilities.length > ITEM_LIMIT &&
                    !expandedLists.includes(sectionIndex) && (
                      <button
                        className={styles.expand}
                        onClick={() =>
                          setExpandedLists(value => [...value, sectionIndex])
                        }
                      >
                        <Typography tagStyle="bodyMedium">
                          {t('items.common.words.viewMore', {
                            count: section.facilities.length - ITEM_LIMIT,
                          })}
                        </Typography>
                      </button>
                    )}
                </Column>
              ))}
            </Row>
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

HotelFacilities.displayName = 'HotelFacilities';

export default HotelFacilities;
